import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';

import { PageShell } from '@/components/layout/PageShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PipelineShell } from '@/components/pipeline/PipelineShell';
import { useAuth } from '@/hooks/useAuth';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export function PipelinePage() {
  return (
    <RequireAuth>
      <PageShell>
        <PipelineInner />
      </PageShell>
    </RequireAuth>
  );
}

function PipelineInner() {
  const { jobId } = useParams<{ jobId: string }>();
  const { token } = useAuth();
  const jobArgs = useMemo((): { token: string; jobId: Id<'jobs'> } | 'skip' => {
    if (typeof token !== 'string' || !token || !jobId) return 'skip';
    return { token, jobId: jobId as Id<'jobs'> };
  }, [token, jobId]);
  const job = useQuery(api.jobs.get, jobArgs);

  if (!jobId) {
    return <p className="text-graphite">Missing job id in URL.</p>;
  }
  if (job === undefined) return <LoadingSpinner label="Loading job…" />;
  if (job === null) {
    return (
      <div className="space-y-2">
        <h2 className="font-serif text-display-md italic text-ink">Job not found.</h2>
      </div>
    );
  }

  const projectId = (job.projectId ?? null) as Id<'projects'> | null;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <p className="smallcaps text-graphite">Pipeline</p>
        <h1 className="font-serif text-display-md italic text-ink">
          Watching the agents work.
        </h1>
        <p className="text-body text-graphite max-w-2xl">
          Each agent runs in turn, with checkpoints where you can review and approve. The pipeline
          waits for you at each checkpoint — no progress without your nod.
        </p>
      </header>

      <ErrorBoundary>
        <PipelineShell jobId={jobId} projectId={projectId} />
      </ErrorBoundary>
    </div>
  );
}
